import classNames from 'classnames';
import { dropRight } from 'lodash';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import CountryStore from '../../../store/CountryStore';
import CurrencyStore from '../../../store/CurrencyStore';
import LanguageStore from '../../../store/LanguageStore';
import UIStore from '../../../store/UIStore';
import { countryCodeFromLocale } from '../../../util/languageMapper';
import LocaleFlagIcon from '../LocaleFlagIcon';

// All flags will be shown when languages count <= max slots.
// Otherwise, max slots flags will be shown. By default, the first flags
// in the array will be displayed, but if the currently selected language is
// not among them, it will replace the last flag.
const MAX_VISIBLE_LANGUAGE_FLAG_SLOTS = 5;

const MobileNavigationRegionalSettingsSummary = ({
  languageStore,
  currencyStore,
  countryStore,
  uiStore,
}) => {
  const items = [];

  const getLanguagesLabel = () => {
    const displayPartialFlagList =
      languageStore.languages.length > MAX_VISIBLE_LANGUAGE_FLAG_SLOTS;

    let displayedFlags = languageStore.languages;

    if (displayPartialFlagList) {
      displayedFlags = displayedFlags.slice(0, MAX_VISIBLE_LANGUAGE_FLAG_SLOTS);
      if (
        !displayedFlags.find(
          (item) => item.code === languageStore.activeLanguage.code
        )
      ) {
        displayedFlags = dropRight(displayedFlags);
        displayedFlags.push(languageStore.activeLanguage);
      }
    }

    return (
      <>
        {displayedFlags.map((language) => {
          const isActive = languageStore.activeLanguage === language;

          return (
            <span
              className={classNames(
                'MobileNavigationRegionalSettingsSummary__flag-container',
                {
                  active: isActive,
                }
              )}
              key={language.code}
            >
              <LocaleFlagIcon
                locale={countryCodeFromLocale(language.code)}
                decorative
              />
            </span>
          );
        })}
        {displayPartialFlagList && (
          <span className="MobileNavigationRegionalSettingsSummary__more">
            +
          </span>
        )}
      </>
    );
  };

  if (countryStore.countries.length > 1) {
    items.push({
      key: 'country',
      label: (
        <>
          <FormattedMessage
            {...globalTranslations.deliveryCountryLabel}
            values={{ country: countryStore.activeCountry.name }}
          />{' '}
          <LocaleFlagIcon
            locale={countryStore.activeCountry.iso_code_2.toLowerCase()}
            decorative
          />
        </>
      ),
    });
  }

  if (languageStore.languages.length > 1) {
    items.push({
      key: 'language',
      label: getLanguagesLabel(),
    });
  }

  if (currencyStore.currencies.length > 1) {
    items.push({
      key: 'currency',
      label: (
        <FormattedMessage
          {...globalTranslations.currencyLabel}
          values={{ currency: currencyStore.currencyCode }}
        />
      ),
    });
  }

  if (items.length === 0) {
    return null;
  }

  const modal = uiStore.mobileRegionalSettingsModal;

  const handleClick = (event) => {
    event.stopPropagation();
    modal.toggle();
  };

  return (
    <button
      type="button"
      className="MobileNavigationRegionalSettingsSummary"
      aria-haspopup="dialog"
      aria-expanded={modal.isOpen}
      aria-controls="mobile-regional-settings-modal"
      onClick={handleClick}
    >
      <span className="sr-only">
        <FormattedMessage {...globalTranslations.regionalSettingsTitle} />
      </span>
      {items.map((item) => (
        <div
          key={item.key}
          className="MobileNavigationRegionalSettingsSummary__selector"
        >
          {item.label}
        </div>
      ))}
    </button>
  );
};

MobileNavigationRegionalSettingsSummary.propTypes = {
  languageStore: modelOf(LanguageStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
};

export default inject(
  'languageStore',
  'currencyStore',
  'countryStore',
  'uiStore'
)(observer(MobileNavigationRegionalSettingsSummary));
